import { api, ApiError } from './api';
import {
  contextoKey,
  descontarStockCache,
  encolarVenta,
  listarVentasOffline,
  actualizarVentaOffline,
  eliminarVentaOffline,
  obtenerDispositivoId,
  siguienteSecuencia,
  leerCache,
  guardarBootstrap,
} from './offlineDb';

export const APP_VERSION = '3.4.0';

function nombreDispositivo() {
  const plataforma = navigator.userAgentData?.platform || navigator.platform || 'Dispositivo';
  return `${plataforma} · ${navigator.userAgent.includes('Mobile') ? 'Móvil' : 'PC'}`.slice(0, 120);
}

export async function descargarDatosOffline(usuario) {
  const dispositivoId = await obtenerDispositivoId();
  const params = new URLSearchParams({
    dispositivo_id: dispositivoId,
    nombre_dispositivo: nombreDispositivo(),
    app_version: APP_VERSION,
  });
  const data = await api.get(`/api/offline/bootstrap?${params.toString()}`);
  await guardarBootstrap(data, usuario);
  // Calienta también el contexto y el Dashboard para que una reapertura de la
  // PWA sin Internet no arranque con una pantalla de error.
  await Promise.allSettled([
    api.get('/api/contextos'),
    api.get('/api/dashboard'),
  ]);
  window.dispatchEvent(new CustomEvent('offline-cache-updated', { detail: data }));
  return data;
}

export async function crearVentaOffline({ usuario, turnoId, payload, preview, clientes = [] }) {
  const dispositivoId = await obtenerDispositivoId();
  const secuencia = await siguienteSecuencia(usuario);
  const bootstrap = await leerCache('/api/offline/bootstrap', usuario);
  const cliente = clientes.find((c) => String(c.id) === String(payload.cliente_id));
  const creadaEn = new Date().toISOString();
  const registro = {
    turno_id_origen: Number.isSafeInteger(Number(turnoId)) && Number(turnoId) > 0 ? Number(turnoId) : null,
    idempotency_key: payload.idempotency_key,
    contexto_key: contextoKey(usuario),
    usuario_id: Number(usuario.id),
    empresa_id: Number(usuario.empresa_id),
    sucursal_id: Number(usuario.sucursal_id),
    deposito_id: Number(usuario.deposito_id),
    dispositivo_id: dispositivoId,
    nombre_dispositivo: nombreDispositivo(),
    secuencia,
    creada_en: creadaEn,
    actualizado_en: creadaEn,
    estado: 'pendiente',
    intentos: 0,
    payload,
    preview,
    cliente_nombre: cliente?.nombre || 'Consumidor Final',
    catalogo_fecha: bootstrap?.data?.catalogo_fecha || bootstrap?.metadata?.catalogo_fecha || null,
    error: null,
    server_result: null,
  };
  await encolarVenta(registro);
  await descontarStockCache(usuario, payload.productos);
  return registro;
}

function offlineMetadata(venta) {
  return {
    dispositivo_id: venta.dispositivo_id,
    nombre_dispositivo: venta.nombre_dispositivo,
    secuencia: venta.secuencia,
    creada_en: venta.creada_en,
    total_esperado: Number(venta.preview?.total_final || 0),
    catalogo_fecha: venta.catalogo_fecha,
    app_version: APP_VERSION,
    turno_id_origen: venta.turno_id_origen || null,
  };
}

export async function sincronizarPendientes(usuario, { onProgress } = {}) {
  if (!navigator.onLine || !usuario) return { sincronizadas: 0, conflictos: 0, pendientes: 0 };
  const ventas = (await listarVentasOffline({ usuario, incluirSincronizadas: false }))
    .filter((v) => v.estado === 'pendiente' || v.estado === 'sincronizando');
  let sincronizadas = 0, conflictos = 0, pendientes = 0;

  for (let i = 0; i < ventas.length; i += 1) {
    const venta = ventas[i];
    onProgress?.({ actual: i + 1, total: ventas.length, venta });
    await actualizarVentaOffline(venta.idempotency_key, { estado: 'sincronizando', error: null, intentos: Number(venta.intentos || 0) + 1 });
    try {
      const result = await api.post('/api/ventas', {
        ...venta.payload,
        offline_metadata: offlineMetadata(venta),
      });
      await actualizarVentaOffline(venta.idempotency_key, {
        estado: 'sincronizada',
        sincronizada_en: new Date().toISOString(),
        server_result: result,
        error: null,
      });
      sincronizadas += 1;
    } catch (err) {
      const apiError = err instanceof ApiError ? err : new ApiError('No se pudo sincronizar la venta.', 0);
      if (apiError.status === 0 || apiError.status >= 500) {
        await actualizarVentaOffline(venta.idempotency_key, { estado: 'pendiente', error: apiError.mensaje });
        pendientes += 1;
        if (apiError.status === 0) break;
      } else if (apiError.status === 401) {
        await actualizarVentaOffline(venta.idempotency_key, { estado: 'pendiente', error: 'Iniciá sesión nuevamente para sincronizar.' });
        pendientes += 1;
        break;
      } else {
        await actualizarVentaOffline(venta.idempotency_key, {
          estado: 'conflicto',
          error: apiError.mensaje,
          error_code: apiError.code || null,
          error_details: apiError.details || null,
        });
        conflictos += 1;
      }
    }
  }

  try {
    const dispositivoId = await obtenerDispositivoId();
    await api.post('/api/offline/sync-completada', { dispositivo_id: dispositivoId, app_version: APP_VERSION });
  } catch { /* no bloquea las ventas ya sincronizadas */ }

  if (sincronizadas > 0) {
    try { await descargarDatosOffline(usuario); } catch { /* el catálogo se refrescará luego */ }
  }
  window.dispatchEvent(new CustomEvent('offline-sync-finished', { detail: { sincronizadas, conflictos, pendientes } }));
  return { sincronizadas, conflictos, pendientes };
}

export async function reintentarVentaOffline(idempotencyKey) {
  return actualizarVentaOffline(idempotencyKey, { estado: 'pendiente', error: null, error_code: null, error_details: null });
}

export async function descartarVentaOffline(idempotencyKey) {
  return eliminarVentaOffline(idempotencyKey);
}
